import React from "react";
import { BrowserRouter, Route, Switch, Redirect } from "react-router-dom";
import { createBrowserHistory } from "history";
import Amplify, { Auth, Hub, API } from "aws-amplify";
import { MuiThemeProvider } from "@material-ui/core/styles";
import { createTheme } from "./utility/Theme";
import { setThemeColors } from "./utility/Theme/helpers";
import Header from "./components/Header/";
import SignInPage from "./components/Account/SignInPage";
import SignOutPage from "./components/Account/SignOutPage";
import ConfirmAccountPage from "./components/Account/ConfirmAccountPage";
import UserAccount from "./components/UserAccount";
import Newsletters from "./components/Newsletters/";
import EmailAlerts from "./components/EmailAlerts";
import TextAlerts from "./components/TextAlerts";
import Contests from "./components/Contests";
import getSsoSesion from "./extended-amplify-auth/SSO";
import {
  handleSsoPostSignInAction,
  handleSsoPostSetSessionAction
} from "./extended-amplify-auth/handleSsoPostActions";
import CONFIG from "./config";
import "./App.css";
import { getTenantConfig, hostnameToStation } from "./utility/tenantConfig";
import { oAuthSignOutRedirect } from "./utility/oAuthSignout";
import Loading from "./components/utils/Loading";
import { AuthenticatorWrapper } from "./extended-amplify-auth";
import ScrollToTop from "./components/utils/ScrollToTop";
import FavIcon from "./components/meta/FavIcon";
import { default as debugLog, LOG_TYPE } from "./utility/logger";

// Figure out the current site.
const site = hostnameToStation(
  window.location.hostname,
  window.location.search
);

// Endpoint will verify user with their JWT Token
const getUserAuthorizationHeader = async () => {
  const authHeader = {
    Authorization: `${(await Auth.currentSession()).getIdToken().jwtToken}`
  };
  if (process.env.REACT_APP_DEPLOY_ENV === "LOCAL") {
    authHeader["cloudfront-key"] = process.env.REACT_APP_MC_CLOUDFRONT_KEY;
  }
  return authHeader;
};

// Some config endpoints will only be secured by Basic Authorizer
const getBasicAuthorizationHeader = () => {
  const authHeader = {
    "x-api-key": CONFIG.apiGateway.APIKEY
  };
  return authHeader;
};

Amplify.configure({
  Auth: {
    mandatorySignIn: true,
    region: CONFIG.cognito.region,
    userPoolId: CONFIG.cognito.userPoolId,
    userPoolWebClientId: CONFIG.cognito.userPoolWebClientId,
    oauth: CONFIG.oauthConfig,
    // TODO: change back to USER_SRP_AUTH once migration complete and uncheck in Cognito
    authenticationFlowType: "USER_PASSWORD_AUTH"
  },
  API: {
    endpoints: [
      {
        name: "accounts",
        endpoint: `${CONFIG.apiGateway.URL}/accounts`,
        region: CONFIG.apiGateway.REGION,
        custom_header: getBasicAuthorizationHeader
      },
      {
        name: "tenantConfigs",
        endpoint: `${CONFIG.apiGateway.URL}/configs`,
        region: CONFIG.apiGateway.REGION,
        custom_header: getBasicAuthorizationHeader
      },
      {
        name: "email",
        endpoint: `${CONFIG.apiGateway.URL}/email`,
        region: CONFIG.apiGateway.REGION,
        custom_header: getBasicAuthorizationHeader
      },
      {
        name: "userEmailAlerts",
        endpoint: `${CONFIG.apiGateway.URL}/emailalerts`,
        region: CONFIG.apiGateway.REGION,
        custom_header: getUserAuthorizationHeader
      },
      {
        name: "extendedProfiles",
        endpoint: `${CONFIG.apiGateway.URL}/profiles`,
        region: CONFIG.apiGateway.REGION,
        custom_header: getUserAuthorizationHeader
      },
      {
        name: "nickname",
        endpoint: `${CONFIG.apiGateway.URL}/profiles/nickname`,
        region: CONFIG.apiGateway.REGION,
        custom_header: getBasicAuthorizationHeader
      },
      {
        name: "userNewsletters",
        endpoint: `${CONFIG.apiGateway.URL}/newsletters`,
        region: CONFIG.apiGateway.REGION,
        custom_header: getUserAuthorizationHeader
      },
      {
        name: "newsletters",
        endpoint: `${CONFIG.apiGateway.URL}/newsletters`,
        region: CONFIG.apiGateway.REGION,
        custom_header: getBasicAuthorizationHeader
      },
      {
        name: "textalerts",
        endpoint: `${CONFIG.apiGateway.URL}/textalerts`,
        region: CONFIG.apiGateway.REGION,
        custom_header: getUserAuthorizationHeader
      }
    ]
  }
});

class App extends React.PureComponent {
  state = {
    loadingTenant: true,
    isAuthenticated: false,
    user: null,
    authState: null,
    tenantConfig: {}
  };

  constructor(props) {
    super(props);
    this.updateAuthProps = this.updateAuthProps.bind(this);
    this.ssoSession = getSsoSesion();
    Hub.listen("auth", this.setupAuthListeners);
  }

  setupAuthListeners = async data => {
    const { payload } = data;
    debugLog("[HUB]", LOG_TYPE.LOG, payload.event, payload.message);
    switch (payload.event) {
      case "cognitoHostedUI": {
        // Amplfiy bug leads to the Authenticator returning from social authentication with a user
        // without attributes.
        // https://github.com/aws-amplify/amplify-js/issues/3214
        // This delayed state check will return the user that has attributes
        const t = setInterval(() => {
          const userEmail =
            this.state.user && this.state.user.attributes
              ? this.state.user.attributes.email
              : null;
          if (userEmail) {
            clearInterval(t);
            this.updateProfileAtSignIn(userEmail);
          }
        }, 500);
        break;
      }
      case "signIn": {
        await this.ssoSession.storeRefreshToken({ authEvent: payload.event });
        await handleSsoPostSignInAction(data, this.ssoSession);

        const userEmail =
          payload.data.attributes && payload.data.attributes.email
            ? payload.data.attributes.email
            : null;
        userEmail && this.updateProfileAtSignIn(userEmail);
        break;
      }
      case "signOut": {
        await this.ssoSession.clearRefreshToken({ authEvent: payload.event });
        if (this.state.tenantConfig.ui) {
          window.location.assign(this.state.tenantConfig.ui.urls.backToStation);
        }
        break;
      }
      case "oAuthSignOut": {
        oAuthSignOutRedirect.preSignoutSetup();
        await this.ssoSession.clearRefreshToken({ authEvent: payload.event });
        break;
      }
      default:
        break;
    }
  };

  updateProfileAtSignIn(userEmail) {
    if (!this.updatedLastSignin) {
      this.updatedLastSignin = true;

      // No need to await this API call. Ok to let it finish after this function returns.
      API.put("extendedProfiles", "/async/", {
        queryStringParameters: {
          email: userEmail
        },
        body: {
          email: userEmail,
          site: site
        }
      });
    }
  }

  componentDidMount = async () => {
    let t = setInterval(async () => {
      const state = this.state;
      if (state.authState && ["signedIn", "signIn"].includes(state.authState)) {
        try {
          await this.ssoSession.syncState({
            authState: state.authState,
            user: state.user
          });
        } catch (e) {
          console.error("SSO Not available, polling cancelled", e);
          clearInterval(t);
        }
      }
    }, 10000);
    await handleSsoPostSetSessionAction(this.ssoSession);
    const tenantConfig = await getTenantConfig(site);
    this.setState({ tenantConfig, loadingTenant: false });

    if (oAuthSignOutRedirect.postSignoutCleanup()) {
      try {
        await this.ssoSession.syncState({ authState: "signIn" });
      } catch (e) {
        console.error("SSO Not available");
      }

      //redirect to domain
      window.location.assign(tenantConfig.ui.urls.backToStation);
    }
  };

  componentWillUnmount() {
    Hub.remove("auth", this.setupAuthListeners);
  }

  updateAuthProps(
    authState,
    authData,
    { lastAuthenticatorUpdate = null } = {}
  ) {
    debugLog("updating state");
    debugLog("lastAuthenticatorUpdate", LOG_TYPE.LOG, lastAuthenticatorUpdate);
    this.setState({
      user: authData,
      isAuthenticated: authState === "signedIn",
      lastAuthenticatorUpdate,
      authState
    });
    debugLog("authState", LOG_TYPE.LOG, authState);
    debugLog("authData", LOG_TYPE.LOG, authData);
  }

  render() {
    // to accommodate s3 static hosting:
    // https://viastudio.com/hosting-a-reactjs-app-with-routing-on-aws-s3/
    const history = createBrowserHistory();
    const path = (/#!(\/.*)$/.exec(window.location.hash) || [])[1];
    if (path) {
      history.replace(path);
    }

    if (this.state.loadingTenant) {
      return (
        <>
          <AuthenticatorWrapper updateAuthProps={this.updateAuthProps} />
          <Loading />
        </>
      );
    } else if (this.state.loadingTenant === false) {
      // set the theme with colors from the tenantConfig
      const theme = createTheme(
        setThemeColors(this.state.tenantConfig.ui.theme.colors)
      );
      const favicon = this.state.tenantConfig.ui.theme.favicon;

      return (
        <div className="App">
          <MuiThemeProvider theme={theme}>
            {/* Set the favicon from the tenant config, if present. */}
            {favicon && <FavIcon href={favicon} />}

            <BrowserRouter>
              {/* Scroll to top of the page on location changes. */}
              <ScrollToTop />

              <Header
                authProps={this.state}
                signInPath={"/sign-in"}
                tenantConfig={this.state.tenantConfig}
              />

              <Route
                render={props => (
                  <AuthenticatorWrapper
                    {...props}
                    authProps={this.state}
                    updateAuthProps={this.updateAuthProps}
                    signInPath={"/sign-in"}
                    tenantConfig={this.state.tenantConfig}
                    site={site}
                  />
                )}
              />
              <div>
                <Switch>
                  <Route exact path="/">
                    <Redirect to="/edit-profile" />
                  </Route>
                  <Route
                    exact
                    path="/sign-in"
                    render={props => (
                      <SignInPage {...props} authProps={this.state} />
                    )}
                  />
                  <Route
                    exact
                    path="/account/logout"
                    render={props => (
                      <SignOutPage {...props} authProps={this.state} />
                    )}
                  />
                  <Route
                    exact
                    path="/confirm-account"
                    render={props => (
                      <ConfirmAccountPage {...props} authProps={this.state} />
                    )}
                  />
                  <Route
                    exact
                    path="/edit-profile"
                    render={props => (
                      <UserAccount
                        {...props}
                        authProps={this.state}
                        updateAuthProps={this.updateAuthProps}
                        site={site}
                      />
                    )}
                  />
                  <Route
                    exact
                    path="/newsletters"
                    render={props => (
                      <Newsletters
                        {...props}
                        tenantConfig={this.state.tenantConfig}
                        authProps={this.state}
                        site={site}
                      />
                    )}
                  />
                  <Route
                    exact
                    path="/email-alerts"
                    render={props => (
                      <EmailAlerts
                        {...props}
                        tenantConfig={this.state.tenantConfig}
                        authProps={this.state}
                      />
                    )}
                  />
                  <Route
                    exact
                    path="/text-alerts"
                    render={props => (
                      <TextAlerts
                        {...props}
                        site={site}
                        updateAuthProps={this.updateAuthProps}
                        authProps={this.state}
                        tenantConfig={this.state.tenantConfig}
                        manageAlertsUrl={
                          this.state.tenantConfig.ui.urls.manageAlerts
                        }
                      />
                    )}
                  />
                  <Route
                    exact
                    path="/contests"
                    render={props => (
                      <Contests
                        {...props}
                        tenantConfig={this.state.tenantConfig}
                        authProps={this.state}
                      />
                    )}
                  />
                </Switch>
              </div>
            </BrowserRouter>
          </MuiThemeProvider>
        </div>
      );
    }
  }
}

export default App;
